import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import * as prismicH from '@prismicio/helpers';
import {
  alignContent,
  getSlicePadding,
  heroVideoPrimaryFields,
  resolveLink,
  secondaryCtaFields,
  toCtaButtonPropsFromItem,
} from '@/utils';
import { asText } from '@prismicio/helpers';
import { PrismicNextLink } from '@prismicio/next';
import { Container, CtaActionsRow, CtaButton, IconSvg } from '@/components';
import classNames from 'classnames';
import style from './herovideovariation.module.scss';
import { VideoSlice, VideoSliceHeroVideo } from 'prismicio-types';
import { VideoOptimizer } from '../Video.component';
import { getVideoWrapperStyle, hasVideoMaxHeight } from '../Video.components';
import { useHeroVideoStore } from '../../../data/state/heroVideo';
import { useVideoNoPosterReveal } from '../useVideoNoPosterReveal';
import { useVideoPlaybackPreserve } from '../useVideoPlaybackPreserve';

export const HeroVideoVariation: FC<{
  slice: VideoSlice & VideoSliceHeroVideo;
  aspectRatio: string;
  isStoreVisible?: boolean;
}> = ({ slice, aspectRatio, isStoreVisible = true }) => {
  const [heroVideoState, setHeroVideoState] = useHeroVideoStore();
  const motionPreference = heroVideoState.heroVideoMotionPreference;
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  const [isTabVisible, setIsTabVisible] = useState(() => {
    if (typeof document === 'undefined') {
      return true;
    }

    return !document.hidden;
  });
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const poster = slice?.primary?.preview_image;
  const posterUrl = prismicH.asImageSrc(poster);
  const videoUrl = prismicH.asLink(slice?.primary?.video_content) || '';
  const hasPoster = Boolean(posterUrl);
  const hasVideoWithoutPoster = Boolean(videoUrl) && !hasPoster;
  const {
    handleVideoPlay,
    handleVideoReady,
    isVideoVisible,
    revealVideoIfBuffered,
    skipRevealTransition,
  } = useVideoNoPosterReveal({
    hasPoster,
    hasVideoWithoutPoster,
    videoRef,
    videoUrl,
  });

  const shouldPlay =
    isStoreVisible &&
    isTabVisible &&
    !prefersReducedMotion &&
    motionPreference !== 'paused';

  const { skipRevealOnRestore } = useVideoPlaybackPreserve(
    videoRef,
    videoUrl,
    shouldPlay,
  );

  const revealInstant =
    isVideoVisible && (skipRevealTransition || skipRevealOnRestore);

  const allowAutoplay = shouldPlay && !skipRevealOnRestore;

  // Reflect autoplay intent immediately so the button does not flash Play → Pause.
  const isPlaying =
    motionPreference === 'playing' ||
    (motionPreference !== 'paused' && shouldPlay);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handlePrefersChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    setIsTabVisible(!document.hidden);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handlePrefersChange);
    } else {
      mediaQuery.addListener(handlePrefersChange);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handlePrefersChange);
      } else {
        mediaQuery.removeListener(handlePrefersChange);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const syncVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    revealVideoIfBuffered();

    if (shouldPlay) {
      void video
        .play()
        .then(() => handleVideoPlay())
        .catch(() => undefined);
      return;
    }

    video.pause();
  };

  useEffect(() => {
    syncVideoPlayback();
  }, [shouldPlay, videoUrl, hasVideoWithoutPoster, isStoreVisible]);

  const handleVideoReadyAndPlay = () => {
    handleVideoReady();

    if (!videoRef.current || !shouldPlay) return;

    if (videoRef.current.paused) {
      void videoRef.current.play().catch(() => undefined);
    }
  };

  const handleTogglePlay = () => {
    if (!videoRef.current) {
      setHeroVideoState({
        heroVideoMotionPreference: isPlaying ? 'paused' : 'playing',
      });
      return;
    }

    if (isPlaying) {
      videoRef.current.pause();
      setHeroVideoState({ heroVideoMotionPreference: 'paused' });
      return;
    }

    void videoRef.current.play();
    setHeroVideoState({ heroVideoMotionPreference: 'playing' });
  };

  const marginBottom = slice?.primary?.bottom_spacing;
  const paddingSection = slice?.primary?.padding_section;
  const background = slice?.primary?.section_background_color ?? '';
  const videoOnFullWidth = slice?.primary?.video_on_full_width;

  const contentDirectionMobile = slice?.primary?.content_direction_mobile;
  const contentDirectionDesktop = slice?.primary?.content_direction_desktop;

  const wrapperStyle = getVideoWrapperStyle(slice.primary ?? {}, {
    aspectRatio,
    noPosterFill: hasVideoWithoutPoster ? '#000' : undefined,
    posterUrl,
  });
  const hasMaxHeight = hasVideoMaxHeight(slice.primary ?? {});

  const videoLink = resolveLink(
    slice.primary?.link,
    slice.primary?.external_link,
    { externalNewTab: false },
  );
  const opensNewTab =
    videoLink.kind !== 'empty' &&
    'target' in videoLink.props &&
    videoLink.props.target === '_blank';
  const destinationLabel = slice.primary?.link?.trim() || 'home';
  const videoLinkAriaLabel =
    videoLink.kind === 'empty'
      ? 'Go to home'
      : opensNewTab
      ? 'Hero video (opens in a new tab)'
      : `Go to ${destinationLabel}`;

  const HeadingTag =
    slice?.primary?.title?.[0]?.type === 'heading1' ? 'h1' : 'h2';

  const title = asText(slice?.primary?.title);
  const description = slice?.primary?.description;
  const headingText = slice?.primary?.heading_text;
  const primaryCta = slice.primary
    ? toCtaButtonPropsFromItem(slice.primary, heroVideoPrimaryFields, 'primary')
    : null;
  const secondaryCta = slice.primary
    ? toCtaButtonPropsFromItem(slice.primary, secondaryCtaFields, 'secondary')
    : null;

  const {
    content_alignment,
    content_width,
    horizontal_position,
    invert_content_alignment_desktop,
    justify_content,
    text_alignment,
    text_color,
    vertical_position,
  } = slice?.primary || {};

  // Content Alignment controls flex cross-axis of inner items; Text Alignment
  // only sets typographic text-align. Invert mirrors Content Alignment on desktop.
  const baseContentAlign = content_alignment ?? text_alignment ?? 'start';
  let alignmentMobile = alignContent(baseContentAlign);
  let alignmentDesktop = alignContent(baseContentAlign);
  if (invert_content_alignment_desktop) {
    if (baseContentAlign === 'start') {
      alignmentMobile = alignContent('start');
      alignmentDesktop = alignContent('end');
    } else if (baseContentAlign === 'end') {
      alignmentMobile = alignContent('end');
      alignmentDesktop = alignContent('start');
    } else {
      alignmentMobile = alignmentDesktop = alignContent('center');
    }
  }

  // Position the content block on the full-height overlay (same pattern as Banner /
  // Hero Image): column flex → justifyContent = vertical, alignItems = horizontal.
  const contentOverlayStyle: CSSProperties = {
    alignItems: alignContent(horizontal_position ?? 'start'),
    justifyContent: alignContent(vertical_position ?? 'start'),
  };

  const descriptionStyle: CSSProperties = {
    '--content-direction-mobile': contentDirectionMobile,
    '--content-direction-desktop': contentDirectionDesktop,
    '--align-items-mobile': alignmentMobile,
    '--align-items-desktop': alignmentDesktop,
    color: text_color ?? '',
    justifyContent: justify_content,
    textAlign: text_alignment,
    ...(content_width && {
      maxWidth: `${content_width}%`,
    }),
  } as CSSProperties;

  const videoWrapperClassName = classNames(style.container__video_wrapper, {
    [style.container__video_wrapper_not_image]: !posterUrl && !videoUrl,
    [style.container__video_wrapper_constrained]: hasMaxHeight,
    [style.container__video_wrapper_no_poster]: hasVideoWithoutPoster,
  });

  const renderClickableVideo = () => {
    const video = (
      <VideoOptimizer
        alt="Hero video"
        autoplay={allowAutoplay}
        brightness={slice?.primary?.video_brightness || 100}
        contrast={slice?.primary?.video_contrast || 100}
        controls="Hide"
        enableKeyboardControls={false}
        isDecorative={true}
        loop={true}
        muted={true}
        onCanPlay={handleVideoReadyAndPlay}
        onLoadedData={handleVideoReadyAndPlay}
        onPlay={handleVideoPlay}
        playsInline={true}
        poster={poster}
        preload="auto"
        videoClass={classNames(style.video, {
          [style.video_ready]: isVideoVisible,
          [style.video_ready_instant]: revealInstant,
        })}
        videoRef={videoRef}
        videoUrl={videoUrl}
      />
    );

    if (videoLink.kind === 'empty') {
      return (
        <PrismicNextLink
          aria-label="Go to home"
          className={videoWrapperClassName}
          href="/"
          style={wrapperStyle}
        >
          {video}
        </PrismicNextLink>
      );
    }

    return (
      <PrismicNextLink
        {...videoLink.props}
        aria-label={videoLinkAriaLabel}
        className={videoWrapperClassName}
        style={wrapperStyle}
      >
        {video}
      </PrismicNextLink>
    );
  };

  const renderVideoContent = () => (
    <>
      <Container
        className={classNames(style.content, {
          [style.content__inside]: !videoOnFullWidth,
        })}
        style={contentOverlayStyle}
      >
        {title || headingText || description || primaryCta || secondaryCta ? (
          <div
            className={classNames(style.content__description, {
              [style.content__description_large]:
                slice?.primary?.hidden_button_mobile,
            })}
            style={descriptionStyle}
          >
            {headingText && (
              <p
                className={style.content__description__heading}
                dangerouslySetInnerHTML={{
                  __html: headingText,
                }}
                style={{
                  // eslint-disable-next-line max-len
                  color: slice.primary.heading_color ?? '',
                }}
              />
            )}
            {title && (
              <HeadingTag
                className={style.content__description__header}
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
                style={{
                  // eslint-disable-next-line max-len
                  color: slice?.primary?.title_color!,
                  lineHeight: `${slice?.primary?.title_line_height}px`,
                }}
              />
            )}
            {description && (
              <p
                className={classNames(style.content__description__descrip)}
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
                style={{
                  // eslint-disable-next-line max-len
                  lineHeight: `${slice?.primary?.description_line_height}px`,
                }}
              />
            )}
            {(primaryCta || secondaryCta) && (
              <CtaActionsRow className={style.content__description__actions}>
                {primaryCta && (
                  <CtaButton
                    className={classNames(style.content__description__action, {
                      [style.content__description__action_hidden_mobile]:
                        slice?.primary?.hidden_button_mobile,
                    })}
                    {...primaryCta}
                  />
                )}
                {secondaryCta && (
                  <CtaButton
                    className={classNames(style.content__description__action, {
                      [style.content__description__action_hidden_mobile]:
                        slice?.primary?.hidden_button_mobile,
                    })}
                    {...secondaryCta}
                  />
                )}
              </CtaActionsRow>
            )}
          </div>
        ) : null}
      </Container>
      {renderClickableVideo()}
      <button
        aria-label={
          isPlaying ? 'Pause background video' : 'Play background video'
        }
        aria-pressed={isPlaying}
        className={classNames(
          style.playPauseButton,
          slice?.primary?.horizontal_position === 'end'
            ? style.playPauseButton_left
            : style.playPauseButton_right,
          slice?.primary?.vertical_position === 'end'
            ? style.playPauseButton_top
            : style.playPauseButton_bottom,
        )}
        onClick={handleTogglePlay}
        type="button"
      >
        <span aria-hidden="true" className={style.playPauseButton__icon}>
          {isPlaying ? (
            <IconSvg name="pause-icon" />
          ) : (
            <IconSvg name="play-icon" />
          )}
        </span>
        <span className={style.playPauseButton__text}>
          {isPlaying ? 'Pause' : 'Play'}
        </span>
      </button>
    </>
  );

  return (
    <section
      className={classNames(getSlicePadding(paddingSection), {
        [style.hero__custom_dimensions_margin]: marginBottom,
      })}
      hidden={!isStoreVisible}
      style={{ background }}
    >
      <div
        className={classNames(style.hero__custom_dimensions, {
          [style.hero__custom_dimensions_container]: !videoOnFullWidth,
        })}
      >
        {renderVideoContent()}
      </div>
    </section>
  );
};
